import { useLocale } from "@calcom/lib/hooks/useLocale";

/**
 * EN/RU switcher for the public booking pages.
 *
 * Lives in its own file (rather than inside Header.tsx, where it started)
 * because it needs two homes: the Booker header on desktop, and the event
 * meta column on mobile. The mobile booker has no header bar to put it in
 * -- its `[grid-area:header]` cell sits *below the calendar*, which is
 * where this ended up rendering orphaned mid-page before 2026-07-25.
 *
 * Writes a `locale` cookie and reloads; the server-side resolver in
 * packages/features/auth/lib/getLocale.ts reads that cookie ahead of the
 * session token, so the choice sticks for signed-in users too.
 */
export function LanguageSwitcher() {
  const { i18n } = useLocale();
  const currentLang = i18n.language?.startsWith("ru") ? "ru" : "en";

  const switchLanguage = (lang: string) => {
    if (lang === currentLang) return;
    // Persist the choice so the server-side locale resolver picks it up on reload.
    document.cookie = `locale=${lang};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
    window.location.reload();
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => switchLanguage("en")}
        aria-label="Switch language to English"
        aria-pressed={currentLang === "en"}
        className={`rounded px-2 py-1 text-sm font-medium ${
          currentLang === "en"
            ? "bg-emphasis text-default"
            : "text-subtle hover:bg-emphasis hover:text-default"
        }`}>
        EN
      </button>
      <button
        type="button"
        onClick={() => switchLanguage("ru")}
        aria-label="Переключить язык на русский"
        aria-pressed={currentLang === "ru"}
        className={`rounded px-2 py-1 text-sm font-medium ${
          currentLang === "ru"
            ? "bg-emphasis text-default"
            : "text-subtle hover:bg-emphasis hover:text-default"
        }`}>
        RU
      </button>
    </div>
  );
}
